/**
 * Challenge Detail Page — hosts the hero, progress tracker, team/daily
 * tabs, leaderboard sidebar, and share actions for a single challenge.
 *
 * Extracted from `ChallengesPage.js` (previously a 1100+ line dual-page
 * file) so each page owns its own state, effects, and JSX.
 */
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  Loader2, Trophy, Users, Calendar, Target, Check, Share2, Plus,
  ChevronLeft, Instagram, Link2, Zap,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { ChallengeLeaderboard } from '../components/social';
import { TeamCard, CreateTeamModal, TeamLeaderboard } from '../components/social/TeamChallengeComponents';
import { DailyChallengeTracker } from '../components/social/DailyChallengeTracker';
import { CHALLENGE_TYPE_CONFIG, LOGO_URL } from '../utils/challengeConfig';

// Copy text to clipboard with a graceful fallback. Returns bool for success.
const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};

/**
 * `handleShare` is intentionally verbose because each social platform has
 * a different capability: Twitter/X accepts prefilled tweet URLs, Instagram
 * & TikTok don't accept prefilled URLs at all so we deep-link the app and
 * put the caption on the clipboard for a one-tap paste.
 */
const shareChallenge = async (platform, challenge) => {
  const shareUrl = window.location.href;
  const shareText = `Join me in the "${challenge?.title}" challenge on Ascend & Surpass!`;
  const hashtags = '#fitness #challenge #AscendAndSurpass';

  // Native OS share sheet first (mobile only)
  if (platform === 'native' && navigator.share) {
    try {
      await navigator.share({
        title: challenge?.title || 'Fitness Challenge',
        text: shareText,
        url: shareUrl,
      });
      toast.success('Shared successfully!');
      return;
    } catch (err) {
      if (err.name === 'AbortError') return;
    }
  }

  const platformOpen = {
    twitter: () => {
      const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}&hashtags=fitness,AscendAndSurpass`;
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    instagram: () => {
      const isMobile = /android|iphone|ipad/i.test(navigator.userAgent);
      const url = isMobile ? 'instagram://app' : 'https://www.instagram.com/';
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    tiktok: () => {
      window.open('https://www.tiktok.com/', '_blank', 'noopener,noreferrer');
    },
  };
  platformOpen[platform]?.();

  const clipboardCopy = {
    twitter: [`${shareText}\n\n${shareUrl}`, 'Opening X — text copied if you need it!'],
    facebook: [`${shareText}\n\n${shareUrl}`, 'Copied for Facebook!'],
    instagram: [`${shareText}\n\n${hashtags}\n\n${shareUrl}`, 'Opening Instagram — text copied, ready to paste!'],
    tiktok: [`${shareText}\n\n${hashtags} #fyp #gym #fitnesschallenge\n\n${shareUrl}`, 'Opening TikTok — text copied, ready to paste!'],
    copy: [shareUrl, 'Link copied!'],
  };
  const entry = clipboardCopy[platform];
  if (!entry) return;

  const [textToCopy, successMessage] = entry;
  const copied = await copyToClipboard(textToCopy);
  if (copied) {
    toast.success(successMessage, {
      description: 'Paste it on your favorite platform',
      duration: 3000,
    });
  } else {
    toast.info('Copy this link:', { duration: 5000, description: shareUrl });
  }
};

export const ChallengeDetailPage = () => {
  const { id } = useParams();
  const { user, api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [challenge, setChallenge] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [leaderboardMonth, setLeaderboardMonth] = useState(null);
  const [userProgress, setUserProgress] = useState(0);
  const [userLifetimeProgress, setUserLifetimeProgress] = useState(0);
  const [isParticipating, setIsParticipating] = useState(false);
  const [participation, setParticipation] = useState(null);
  const [teams, setTeams] = useState([]);
  const [teamLeaderboard, setTeamLeaderboard] = useState([]);
  const [userTeam, setUserTeam] = useState(null);
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [activeTab, setActiveTab] = useState('overview'); // overview | teams | tracker

  useEffect(() => {
    fetchChallenge();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // `silent=true` skips the loading spinner so background refreshes (after
  // logging daily progress) don't unmount DailyChallengeTracker and wipe
  // its local celebrationQueue / suppress the milestone modal.
  const fetchChallenge = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const res = await api().get(`/challenges/${id}`);
      setChallenge(res.data.challenge);
      setLeaderboard(res.data.leaderboard || []);
      setAvailableMonths(res.data.available_months || []);
      setLeaderboardMonth(res.data.leaderboard_month || null);
      setUserProgress(res.data.user_progress || 0);
      setUserLifetimeProgress(res.data.user_lifetime_progress || 0);
      setIsParticipating(res.data.user_participating);

      if (res.data.user_participating) {
        setParticipation({
          current_value: res.data.user_progress || 0,
          completed: res.data.user_completed || false,
          user_target: res.data.user_target,
          is_personal_target: res.data.is_personal_target,
          joined_at: res.data.joined_at,
          achieved_milestones: res.data.achieved_milestones || [],
        });
      }

      if (res.data.challenge?.is_team_challenge) {
        await fetchTeamData();
      }
    } catch {
      toast.error('Failed to load challenge');
    }
    if (!silent) setLoading(false);
  };

  const fetchTeamData = async () => {
    try {
      const [teamsRes, leaderboardRes] = await Promise.all([
        api().get(`/challenges/${id}/teams`),
        api().get(`/challenges/${id}/team-leaderboard`),
      ]);
      setTeams(teamsRes.data.teams || []);
      setUserTeam(teamsRes.data.user_team);
      setTeamLeaderboard(leaderboardRes.data.leaderboard || []);
    } catch (e) {
      console.debug('Error:', e?.message);
    }
  };

  const handleRestart = async () => {
    try {
      await api().post(`/challenges/${id}/restart`);
      toast.success('Fresh start! Your lifetime total is saved. 💪');
      fetchChallenge({ silent: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not restart');
    }
  };

  const handleJoin = async () => {
    try {
      await api().post(`/challenges/${id}/join`);
      toast.success('Joined challenge!');
      fetchChallenge();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to join challenge');
    }
  };

  const handleLeave = async () => {
    if (!window.confirm('Leave this challenge? Your progress will be kept but you will stop appearing on the leaderboard.')) return;
    try {
      await api().post(`/challenges/${id}/leave`);
      toast.success('Left challenge');
      fetchChallenge();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to leave challenge');
    }
  };

  const handleShare = (platform) => shareChallenge(platform, challenge);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="h-12 w-12 text-primary animate-spin" />
      </div>
    );
  }
  if (!challenge) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <p className="text-white">Challenge not found</p>
      </div>
    );
  }

  const config = CHALLENGE_TYPE_CONFIG[challenge.challenge_type] || CHALLENGE_TYPE_CONFIG.workout_count;
  const ChallengeIcon = config.icon;
  // Late joiners have a smaller personal goal for this month only.
  const goal = (isParticipating && participation?.user_target) || challenge.target_value || 0;
  const isPersonalGoal = isParticipating && participation?.is_personal_target;
  const progressPct = goal > 0
    ? Math.min(100, (userProgress / goal) * 100)
    : 0;
  const isActive = new Date(challenge.start_date) <= new Date() && new Date(challenge.end_date) >= new Date();
  const hasEnded = new Date(challenge.end_date) < new Date();
  const isTeamChallenge = challenge.is_team_challenge;
  const isDailyChallenge = ['daily_water', 'daily_reps', 'daily_duration'].includes(challenge.challenge_type);

  const daysLeft = isActive
    ? Math.max(1, Math.ceil((new Date(challenge.end_date) - new Date()) / 86400000))
    : 0;
  const dailyPace = challenge.daily_target || 0;
  const lateJoinGoal = dailyPace > 0 && daysLeft > 0 ? Math.min(challenge.target_value || 0, dailyPace * daysLeft) : 0;
  const paceUnit = challenge.unit || config.unit || 'units';

  return (
    <div className="min-h-screen bg-black" data-testid="challenge-detail-page">
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/challenges"
            data-testid="challenge-detail-back"
            className="group inline-flex items-center gap-1.5 pl-2.5 pr-3.5 py-2 rounded-full text-white transition-all
              bg-gradient-to-b from-card to-card/70 border border-white/15
              shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_4px_12px_rgba(0,0,0,0.4)]
              hover:border-primary/60 hover:text-primary hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_6px_18px_rgba(212,175,55,0.18)]
              active:scale-95"
          >
            <ChevronLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" strokeWidth={2.5} />
            <span className="font-heading text-[11px] uppercase tracking-[0.18em]">Back</span>
          </Link>
          <img src={LOGO_URL} alt="Ascend & Surpass" className="h-10 w-10" />
          <div className="flex gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" data-testid="challenge-share-btn">
                  <Share2 className="h-4 w-4 mr-2" /> Share
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-card border-border min-w-[180px]">
                <DropdownMenuItem onClick={() => handleShare('twitter')} className="cursor-pointer focus:bg-primary/10" data-testid="share-x-option">
                  <span className="inline-flex items-center justify-center h-5 w-5 mr-2 rounded bg-black text-white text-[10px] font-bold">𝕏</span>
                  Share to X
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleShare('instagram')} className="cursor-pointer focus:bg-primary/10" data-testid="share-instagram-option">
                  <span className="inline-flex items-center justify-center h-5 w-5 mr-2 rounded bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 text-white">
                    <Instagram className="h-3 w-3" />
                  </span>
                  Share to Instagram
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleShare('tiktok')} className="cursor-pointer focus:bg-primary/10" data-testid="share-tiktok-option">
                  <span className="inline-flex items-center justify-center h-5 w-5 mr-2 rounded bg-black text-white text-[10px] font-bold">♪</span>
                  Share to TikTok
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-border" />
                <DropdownMenuItem onClick={() => handleShare('copy')} className="cursor-pointer focus:bg-primary/10" data-testid="share-copy-option">
                  <Link2 className="h-4 w-4 mr-2 text-muted-foreground" />
                  Copy link
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        {(isTeamChallenge || (isDailyChallenge && isParticipating)) && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex gap-2 mb-6 flex-wrap"
          >
            <Button
              variant={activeTab === 'overview' ? 'default' : 'outline'}
              onClick={() => setActiveTab('overview')}
              className={`rounded-full px-6 py-2.5 h-auto font-heading uppercase tracking-[0.18em] text-sm ${activeTab === 'overview' ? 'bg-primary text-black' : ''}`}
            >
              Overview
            </Button>
            {isTeamChallenge && (
              <Button
                variant={activeTab === 'teams' ? 'default' : 'outline'}
                onClick={() => setActiveTab('teams')}
                className={`rounded-full px-6 py-2.5 h-auto font-heading uppercase tracking-[0.18em] text-sm ${activeTab === 'teams' ? 'bg-primary text-black' : ''}`}
              >
                <Users className="h-4 w-4 mr-2" />
                Teams ({teams.length})
              </Button>
            )}
            {isDailyChallenge && isParticipating && (
              <Button
                variant="default"
                onClick={() => setActiveTab('tracker')}
                className="group relative overflow-hidden rounded-full border border-blue-300/40 px-6 py-2.5 h-auto text-white font-heading uppercase tracking-[0.18em] text-sm
                  bg-gradient-to-b from-sky-400 to-blue-700
                  shadow-[inset_0_1px_0_rgba(255,255,255,0.35),0_8px_24px_rgba(56,189,248,0.45)]
                  hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.5),0_10px_32px_rgba(56,189,248,0.7)]
                  active:scale-[0.97] transition-all"
                data-testid="challenge-log-progress-btn"
              >
                <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent group-hover:translate-x-full transition-transform duration-700" />
                <Zap className="h-5 w-5 mr-2 fill-white drop-shadow-[0_0_6px_rgba(255,255,255,0.9)] animate-pulse" strokeWidth={2} />
                Log Progress
              </Button>
            )}
          </motion.div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Challenge Info */}
          <div className="lg:col-span-2 space-y-6">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-card border border-border">
              <div className="relative h-48 bg-gradient-to-br from-primary/30 to-primary/5 flex items-center justify-center">
                <ChallengeIcon className="h-24 w-24 text-primary/50" />
                <div className="absolute top-4 right-4 flex gap-2">
                  {isTeamChallenge && <Badge className="bg-purple-500/20 text-purple-400">Team Challenge</Badge>}
                  {hasEnded ? (
                    <Badge className="bg-gray-500/20 text-gray-400">Ended</Badge>
                  ) : isActive ? (
                    <Badge className="bg-green-500/20 text-green-400">Active</Badge>
                  ) : (
                    <Badge className="bg-yellow-500/20 text-yellow-400">Upcoming</Badge>
                  )}
                </div>
              </div>

              <div className="p-6">
                <h1 className="font-heading text-3xl text-white uppercase tracking-wider mb-2">{challenge.title}</h1>
                <p className="text-muted-foreground mb-6">{challenge.description}</p>

                <div className={`grid ${isTeamChallenge ? 'grid-cols-4' : 'grid-cols-3'} gap-4 mb-6`}>
                  <div className="bg-white/5 p-4 text-center">
                    <ChallengeIcon className="h-6 w-6 text-primary mx-auto mb-2" />
                    <div className="text-xl font-bold text-white">{challenge.target_value}</div>
                    <div className="text-xs text-muted-foreground">{config.unit}</div>
                  </div>
                  <div className="bg-white/5 p-4 text-center">
                    <Calendar className="h-6 w-6 text-primary mx-auto mb-2" />
                    <div className="text-xl font-bold text-white">{challenge.duration_days}</div>
                    <div className="text-xs text-muted-foreground">days</div>
                  </div>
                  <div className="bg-white/5 p-4 text-center">
                    <Users className="h-6 w-6 text-primary mx-auto mb-2" />
                    <div className="text-xl font-bold text-white">{isTeamChallenge ? teams.length : leaderboard.length}</div>
                    <div className="text-xs text-muted-foreground">{isTeamChallenge ? 'teams' : 'participants'}</div>
                  </div>
                  {isTeamChallenge && (
                    <div className="bg-white/5 p-4 text-center">
                      <Target className="h-6 w-6 text-primary mx-auto mb-2" />
                      <div className="text-xl font-bold text-white">{challenge.max_team_size || 5}</div>
                      <div className="text-xs text-muted-foreground">max team size</div>
                    </div>
                  )}
                </div>

                {isTeamChallenge && userTeam && (
                  <div className="bg-purple-500/10 border border-purple-500/30 p-4 mb-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Users className="h-5 w-5 text-purple-400" />
                        <div>
                          <span className="text-white font-medium">Your Team: </span>
                          <span className="text-purple-400">{userTeam.name}</span>
                        </div>
                      </div>
                      <Link to={`/challenges/${id}/team/${userTeam.id}`}>
                        <Button size="sm" className="bg-purple-500 text-white hover:bg-purple-600">View Team</Button>
                      </Link>
                    </div>
                    <div className="mt-3">
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-muted-foreground">Team Progress</span>
                        <span className="text-purple-400 font-medium">{userTeam.total_progress} / {userTeam.team_target}</span>
                      </div>
                      <Progress value={userTeam.progress_percentage || 0} className="h-2" />
                    </div>
                  </div>
                )}

                {!isTeamChallenge && isParticipating && (
                  <div className="bg-primary/10 border border-primary/30 rounded-2xl p-4 mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-medium">Your Progress</span>
                      <span className="text-primary font-bold">{userProgress} / {goal}</span>
                    </div>
                    <Progress value={progressPct} className="h-3" />
                    {userLifetimeProgress > userProgress && (
                      <div className="mt-3 flex items-center justify-between bg-black/40 border border-primary/20 rounded-lg px-3 py-2">
                        <span className="text-[11px] uppercase tracking-wider text-muted-foreground">Lifetime total</span>
                        <span className="text-primary font-heading text-sm" data-testid="challenge-lifetime-total">
                          {userLifetimeProgress.toLocaleString()} {paceUnit}
                        </span>
                      </div>
                    )}
                    {isPersonalGoal && (
                      <p className="mt-2 text-xs text-white/60" data-testid="challenge-personal-goal">
                        Your goal: <b className="text-primary">{goal.toLocaleString()} {paceUnit}</b> (joined {new Date(participation.joined_at || Date.now()).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}). Next month it goes back to {challenge.target_value.toLocaleString()}.
                      </p>
                    )}
                    {userProgress >= goal && goal > 0 && (
                      <div className="mt-3 flex items-center gap-2 text-green-400">
                        <Check className="h-5 w-5" />
                        <span className="font-medium">Challenge Completed!</span>
                        <Button
                          size="sm"
                          onClick={handleRestart}
                          className="ml-auto rounded-full bg-green-500 text-black hover:bg-green-400"
                          data-testid="challenge-restart-btn"
                        >
                          Start again
                        </Button>
                      </div>
                    )}
                    {dailyPace > 0 && userProgress < goal && (
                      <div className="mt-3 flex items-center gap-2 text-yellow-300" data-testid="challenge-catchup-pace">
                        <Zap className="h-4 w-4" />
                        <span className="text-sm">
                          Daily goal: <span className="font-bold text-yellow-200">{dailyPace.toLocaleString()} {paceUnit}/day</span>
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {!isTeamChallenge && !isParticipating && isActive && dailyPace > 0 && (
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-2xl p-4 mb-6 flex items-start gap-3" data-testid="challenge-preview-pace">
                    <Zap className="h-5 w-5 text-yellow-300 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="text-yellow-200 font-medium">Joining late? No problem.</p>
                      <p className="text-yellow-100/80 mt-0.5">
                        Just do the normal <span className="font-bold text-yellow-200">{dailyPace.toLocaleString()} {paceUnit}/day</span>. Your goal becomes{' '}
                        <span className="font-bold text-yellow-200">{lateJoinGoal.toLocaleString()} {paceUnit}</span> for the {daysLeft} {daysLeft === 1 ? 'day' : 'days'} left.
                      </p>
                    </div>
                  </div>
                )}

                {!hasEnded && (
                  <div className="flex flex-wrap gap-3 mb-4">
                    {isTeamChallenge ? (
                      !userTeam ? (
                        <Button onClick={() => setShowCreateTeam(true)} className="bg-primary text-black">
                          <Plus className="h-4 w-4 mr-2" />
                          Create a Team
                        </Button>
                      ) : null
                    ) : (
                      !isParticipating ? (
                        <Button onClick={handleJoin} className="bg-primary text-black" size="lg">
                          Join This Challenge
                        </Button>
                      ) : (
                        <Button
                          onClick={handleLeave}
                          variant="outline"
                          size="lg"
                          className="border-red-500/50 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                          data-testid="challenge-leave-btn"
                        >
                          Leave Challenge
                        </Button>
                      )
                    )}
                  </div>
                )}

                <div className="flex flex-wrap gap-2 mt-4">
                  {typeof navigator !== 'undefined' && navigator.share && (
                    <Button variant="default" size="sm" onClick={() => handleShare('native')} className="bg-primary text-black">
                      <Share2 className="h-4 w-4 mr-2" />
                      Share
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => handleShare('copy')}>
                    Copy Link
                  </Button>
                </div>
              </div>
            </motion.div>

            {isTeamChallenge && activeTab === 'teams' && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="font-heading text-xl text-white uppercase tracking-wider">All Teams</h2>
                  {!userTeam && !hasEnded && (
                    <Button onClick={() => setShowCreateTeam(true)} className="bg-primary text-black">
                      <Plus className="h-4 w-4 mr-2" />
                      Create Team
                    </Button>
                  )}
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  {teams.map((team) => (
                    <TeamCard
                      key={team.id}
                      team={team}
                      challenge={challenge}
                      api={api}
                      currentUserId={user?.id}
                      onUpdate={() => { fetchChallenge(); fetchTeamData(); }}
                    />
                  ))}
                </div>
                {teams.length === 0 && (
                  <div className="bg-card border border-border p-8 text-center">
                    <Users className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">No teams yet. Be the first to create one!</p>
                  </div>
                )}
              </motion.div>
            )}

            {isDailyChallenge && activeTab === 'tracker' && isParticipating && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                <DailyChallengeTracker
                  challenge={challenge}
                  participation={participation}
                  api={api}
                  onUpdate={() => fetchChallenge({ silent: true })}
                />
              </motion.div>
            )}
          </div>

          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
            {isTeamChallenge ? (
              <TeamLeaderboard teams={teamLeaderboard} challenge={challenge} currentUserId={user?.id} />
            ) : (
              <ChallengeLeaderboard
                leaderboard={leaderboard}
                challenge={challenge}
                currentUserId={user?.id}
                isAdmin={user?.is_admin === true}
                api={api}
                onChanged={fetchChallenge}
                availableMonths={availableMonths}
                initialMonth={leaderboardMonth}
              />
            )}
          </motion.div>
        </div>
      </main>

      {isTeamChallenge && (
        <CreateTeamModal
          isOpen={showCreateTeam}
          onClose={() => setShowCreateTeam(false)}
          challengeId={id}
          api={api}
          onCreated={() => { fetchChallenge(); fetchTeamData(); }}
        />
      )}
    </div>
  );
};

export default ChallengeDetailPage;
